import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Loader2, Trash2, Send, Check, X } from "lucide-react";
import { api } from "@/lib/api";
import { WbsItemSelect } from "@/components/procurement/WbsItemSelect";
import { DeleteDocButton } from "@/components/procurement/DeleteDocButton";
import { ApprovalReviewButton } from "@/components/procurement/ApprovalReview";
import { ItemPicker } from "@/components/procurement/ItemPicker";
import { ProjectFilter, useProjectFilter } from "@/components/procurement/ProjectFilter";
import { useAuth } from "@/context/AuthContext";
import { useCompany } from "@/context/CompanyContext";
import { PageHeader } from "@/components/PageHeader";
import { EmptyState } from "@/components/EmptyState";
import { StatusPill } from "@/components/StatusPill";
import { DocumentFlowButton } from "@/components/DocumentFlowDialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { getErrorMessage } from "@/lib/errors";

const WRITE_ROLES = ["ORG_ADMIN", "PROCUREMENT"];
const EMPTY_ITEM = { description: "", quantity: "", rate: "" };

export default function WorkOrders() {
  const pf = useProjectFilter();
  const { user } = useAuth();
  const canWrite = WRITE_ROLES.includes(user?.role);
  const { companies } = useCompany() || {};
  const [wos, setWos] = useState([]);
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [woRes, vendorRes, projRes] = await Promise.all([
        api.get("/procurement/work-orders"), api.get("/procurement/vendors"), api.get("/projects").catch(() => ({ data: [] })),
      ]);
      setWos(woRes.data);
      setVendors(vendorRes.data);
      setProjects(projRes.data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const vendorName = (id) => vendors.find((v) => v.id === id)?.name || id;

  const linkProject = async (wo, projectId) => {
    try {
      await api.patch(`/procurement/work-orders/${wo.id}/project`, { project_id: projectId });
      toast.success(projectId ? "Work order linked — its subcontract cost now counts in that project" : "Work order unlinked from project");
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  const act = async (wo, action) => {
    setBusyId(wo.id);
    try {
      await api.post(`/procurement/work-orders/${wo.id}/${action}`, action === "submit" ? undefined : {});
      toast.success(`Work order ${action}ed`);
      load();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="work-orders-page">
      <PageHeader title="Work Orders" description="Sub-contract work orders — routed through the same numbering & approval engine as a PO." action={<div className="flex flex-wrap items-center justify-end gap-2"><ProjectFilter filter={pf} />{canWrite && <CreateWODialog companies={companies} vendors={vendors} projects={projects} onCreated={load} />}</div>} />
      <div className="rounded-xl border border-border bg-card">
        {!loading && wos.length === 0 ? (
          <EmptyState title="No work orders yet" testId="work-orders-empty-state" />
        ) : (
          <Table>
            <TableHeader><TableRow><TableHead>Number</TableHead><TableHead>Project</TableHead><TableHead>Vendor</TableHead><TableHead>Date</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {pf.apply(wos, (r) => r.project_id).map((wo) => (
                <TableRow key={wo.id} data-testid={`wo-row-${wo.number}`}>
                  <TableCell className="font-mono text-xs text-foreground">{wo.number}</TableCell>
                  <TableCell className="min-w-[10rem]">
                    {canWrite && wo.status !== "CANCELLED" ? (
                      <Select value={wo.project_id || "none"} onValueChange={(v) => linkProject(wo, v === "none" ? null : v)}>
                        <SelectTrigger className={`h-8 text-xs ${wo.project_id ? "" : "border-amber-500/60 text-amber-600 dark:text-amber-400"}`} data-testid={`wo-project-select-${wo.number}`}>
                          <SelectValue placeholder="Not linked" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="none">Not linked</SelectItem>
                          {projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    ) : (
                      <span className="text-xs text-muted-foreground">{projects.find((p) => p.id === wo.project_id)?.code || "—"}</span>
                    )}
                  </TableCell>
                  <TableCell className="text-foreground">{vendorName(wo.vendor_id)}</TableCell>
                  <TableCell className="text-muted-foreground">{wo.wo_date}</TableCell>
                  <TableCell className="text-right font-mono text-xs font-semibold text-foreground">{wo.total_amount}</TableCell>
                  <TableCell><StatusPill value={wo.status} /></TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1.5">
                      {canWrite && wo.status === "DRAFT" && <Button size="sm" variant="outline" disabled={busyId === wo.id} onClick={() => act(wo, "submit")} data-testid={`submit-wo-button-${wo.number}`}><Send className="h-3.5 w-3.5" /></Button>}
                      {wo.status === "SUBMITTED" && (
                        <>
                          <Button size="sm" variant="outline" disabled={busyId === wo.id} onClick={() => act(wo, "approve")} data-testid={`approve-wo-button-${wo.number}`}><Check className="h-3.5 w-3.5 text-emerald-500" /></Button>
                          <Button size="sm" variant="outline" disabled={busyId === wo.id} onClick={() => act(wo, "reject")} data-testid={`reject-wo-button-${wo.number}`}><X className="h-3.5 w-3.5 text-rose-400" /></Button>
                        </>
                      )}
                      <ApprovalReviewButton docType="WO" docId={wo.id} onChanged={load} />
                      {canWrite && <DeleteDocButton path={`/procurement/work-orders/${wo.id}`} label="Work order" number={wo.number} status={wo.status} onDeleted={load} />}
                      <DocumentFlowButton docType="WORK_ORDER" docId={wo.id} cancelEndpoint={wo.status === "APPROVED" ? `/procurement/work-orders/${wo.id}/cancel` : undefined} onCancelled={load} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
}

function CreateWODialog({ companies, vendors, projects, onCreated }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ company_id: "", project_id: "", vendor_id: "", wo_date: "", site_location: "", tax_pct: "0" });
  const [items, setItems] = useState([{ ...EMPTY_ITEM }]);
  const [saving, setSaving] = useState(false);
  const [requisitions, setRequisitions] = useState([]);
  const [sourceSr, setSourceSr] = useState("");
  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });
  const updateItem = (idx, field, value) => setItems(items.map((it, i) => (i === idx ? { ...it, [field]: value } : it)));
  // Picking a catalogue item fills description, unit and price (price can still be changed).
  const pickItem = (idx, item) => setItems(items.map((it, i) => (i === idx ? {
    ...it, item_id: item.id, description: item.name, uom_id: item.uom_id || it.uom_id || null, rate: Number(item.standard_rate) > 0 ? String(item.standard_rate) : it.rate,
  } : it)));

  // Approved service requisitions that haven't become a Work Order yet
  useEffect(() => {
    if (!open) return;
    api.get("/procurement/service-requisitions")
      .then(({ data }) => setRequisitions((data || []).filter((r) => r.status === "APPROVED" && !r.work_order_id)))
      .catch(() => setRequisitions([]));
  }, [open]);

  const copyFrom = (srId) => {
    setSourceSr(srId === "none" ? "" : srId);
    const sr = requisitions.find((r) => r.id === srId);
    if (!sr) return;
    setForm((f) => ({ ...f, company_id: sr.company_id || f.company_id, project_id: sr.project_id || f.project_id }));
    setItems(sr.items.map((i) => ({ ...EMPTY_ITEM, description: i.description, quantity: String(Number(i.quantity)), rate: "" })));
    toast.info("Scope copied — now choose the contractor and enter a rate for each line");
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: wo } = await api.post("/procurement/work-orders", { ...form, project_id: form.project_id || null, items: items.map((it) => ({ ...it, wbs_item_id: it.wbs_item_id || null })) });
      if (sourceSr) {
        try {
          await api.post(`/procurement/service-requisitions/${sourceSr}/link-work-order`, { work_order_id: wo.id });
        } catch (linkErr) {
          toast.warning(`Work order created, but the requisition could not be marked converted: ${getErrorMessage(linkErr)}`);
        }
      }
      setSourceSr("");
      toast.success(sourceSr ? `Work order ${wo.number} created from the requisition` : "Work order created");
      setForm({ company_id: "", project_id: "", vendor_id: "", wo_date: "", site_location: "", tax_pct: "0" });
      setItems([{ ...EMPTY_ITEM }]);
      setOpen(false);
      onCreated?.();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button data-testid="create-wo-button"><Plus className="h-4 w-4" /> New Work Order</Button></DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader><DialogTitle>New Work Order</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3.5" data-testid="create-wo-form">
          <div className="rounded-lg border border-dashed border-border p-3">
            <Label>Copy from service requisition <span className="font-normal text-muted-foreground">(fills company, project and work lines)</span></Label>
            <Select value={sourceSr || "none"} onValueChange={copyFrom}>
              <SelectTrigger className="mt-1.5 [&>span]:truncate" data-testid="wo-source-sr-select"><SelectValue placeholder="Start blank" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Start blank</SelectItem>
                {requisitions.map((r) => <SelectItem key={r.id} value={r.id}>{r.number} · {r.scope_of_work || `${r.items.length} line(s)`}</SelectItem>)}
              </SelectContent>
            </Select>
            {requisitions.length === 0 && <p className="mt-1 text-[11px] text-muted-foreground">No approved requisitions waiting. Submit and approve one on the Service Requisitions page.</p>}
          </div>
          <div className="grid grid-cols-3 gap-3">
            <div>
              <Label>Company</Label>
              <Select required value={form.company_id} onValueChange={(v) => setForm({ ...form, company_id: v })}>
                <SelectTrigger data-testid="wo-company-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(companies || []).map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label>Vendor</Label>
              <Select required value={form.vendor_id} onValueChange={(v) => setForm({ ...form, vendor_id: v })}>
                <SelectTrigger data-testid="wo-vendor-select" className="mt-1.5"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{(vendors || []).map((v) => <SelectItem key={v.id} value={v.id}>{v.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date</Label><Input required type="date" data-testid="wo-date-input" value={form.wo_date} onChange={set("wo_date")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Project <span className="font-normal text-muted-foreground">(its subcontract cost is booked there)</span></Label>
            <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
              <SelectTrigger data-testid="wo-project-select" className="mt-1.5"><SelectValue placeholder="Select project" /></SelectTrigger>
              <SelectContent>{(projects || []).map((p) => <SelectItem key={p.id} value={p.id}>{p.code} — {p.name}</SelectItem>)}</SelectContent>
            </Select>
            {!form.project_id && <p className="mt-1 text-[11px] text-amber-600 dark:text-amber-400">Without a project, this work order's cost won't appear in any project's finance.</p>}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Site location</Label><Input data-testid="wo-site-input" value={form.site_location} onChange={set("site_location")} className="mt-1.5" /></div>
            <div><Label>Tax %</Label><Input type="number" step="0.01" data-testid="wo-tax-input" value={form.tax_pct} onChange={set("tax_pct")} className="mt-1.5" /></div>
          </div>
          <div>
            <Label>Items</Label>
            <p className="mt-1 text-[11px] text-muted-foreground">Link each line to a WBS activity so its cost shows phase-wise in Cost to Completion.</p>
            <div className="mt-1.5 space-y-2">
              {items.map((it, idx) => (
                <div key={idx} className="space-y-1 rounded-md border border-transparent p-1 hover:border-border" data-testid={`wo-item-row-${idx}`}>
                  <div className="flex items-center gap-2">
                    <ItemPicker required value={it.description} onChange={(v) => updateItem(idx, "description", v)} onPick={(item) => pickItem(idx, item)} prefer="SERVICE" data-testid={`wo-item-desc-input-${idx}`} />
                    <Input required type="number" step="0.001" placeholder="Qty" className="w-24" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} data-testid={`wo-item-qty-input-${idx}`} />
                    <Input required type="number" step="0.01" placeholder="Rate" className="w-24" value={it.rate} onChange={(e) => updateItem(idx, "rate", e.target.value)} data-testid={`wo-item-rate-input-${idx}`} />
                    {items.length > 1 && <Button type="button" size="sm" variant="ghost" onClick={() => setItems(items.filter((_, i) => i !== idx))}><Trash2 className="h-3.5 w-3.5 text-rose-400" /></Button>}
                  </div>
                  <div className="flex items-center gap-2 pl-0.5">
                    <span className="w-20 shrink-0 text-[11px] text-muted-foreground">WBS activity</span>
                    <div className="max-w-xs flex-1"><WbsItemSelect projectId={form.project_id} value={it.wbs_item_id} onChange={(v) => updateItem(idx, "wbs_item_id", v)} testId={`wo-item-wbs-select-${idx}`} /></div>
                  </div>
                </div>
              ))}
            </div>
            <Button type="button" size="sm" variant="outline" className="mt-2" onClick={() => setItems([...items, { ...EMPTY_ITEM }])} data-testid="add-wo-item-button"><Plus className="h-3.5 w-3.5" /> Add item</Button>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving} data-testid="create-wo-submit-button">{saving && <Loader2 className="h-4 w-4 animate-spin" />} Create</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
